import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import { makeStyles } from '@material-ui/core'
import {
  useThingConfigurationCategories,
  useCreateThingConfiguration
} from '../../../../../../api/things'
import SydModal from '../../../../../commonDesign/SydModal'
import SydLabel, { hookFormErrorAdapter } from '../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../commonDesign/SydInput'
import ErrorDisplay from '../../../../../molecules/ErrorDisplay'
import SaveCancelButtons from '../../../../../molecules/SaveCancelButtons'
import Select from '../../../../../molecules/Select'
import Loading from '../../../../../molecules/Loading'

export const modalStyles = makeStyles(() => ({
  root: {
    height: '25rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  inputRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  selectField: {
    width: '100%',
    minWidth: '300px',
    minHeight: '52px'
  },
  buttonRow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center'
  },
  fullRow: {
    flex: 1
  }
}))

const ThingAddModal = ({ open, onClose }) => {
  const classes = modalStyles()
  const { mutateAsync: addThingSchema, isLoading, isError, error } = useCreateThingConfiguration()
  const levelOptions = [
    { label: 'Account', value: 1 },
    { label: 'Product', value: 3 },
    { label: 'Client', value: 201 }
  ]
  const { data: categories, isLoading: isLoadingCategories } = useThingConfigurationCategories()
  const [customCategory, setCustomCategory] = useState('')
  const categoryOptions = useMemo(() => {
    const options = []

    // Add categories from API if available
    if (categories && Array.isArray(categories.schema)) {
      options.push(...categories.schema.map(item => ({
        label: item.category, // Adjust based on your response structure
        value: item.category
      })))
    }

    // Always add the "Add category" option at the end
    options.push({ label: 'Add category', value: '' })

    return options
  }, [categories])
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      name: '',
      description: '',
      code: '',
      category: categoryOptions[0].value,
      levelTypeId: levelOptions[0].value,
      categoryDescription: ''
    }
  })
  const categoryValue = form.watch('category')
  const showCustomInput = categoryValue === ''
  useEffect(() => {
    if (categories && categories.schema && categories.schema.length > 0) {
      form.setValue('category', categories.schema[0].category)
    }
  }, [categories, form])

  const onSubmit = useCallback(async (data) => {
    const submitData = {
      ...data,
      category: showCustomInput ? customCategory : data.category,
      configuration: {
        contentType: 'markdown'
      }
    }
    const result = await addThingSchema(submitData)
    if (result && result.success) {
      form.reset()
      setCustomCategory('')
      onClose()
    }
  }, [onClose, addThingSchema, form, showCustomInput, customCategory])

  return (
    <SydModal open={open} onClose={onClose} title='Content Add'>
      {isLoadingCategories ? (
        <Loading />
      ) : (
        <>
          <div className={classes.inputRow}>
            <Controller
              name='levelTypeId'
              control={form.control}
              rules={{ required: { value: true, message: 'Level is required' } }}
              render={({ field, fieldState }) => (
                <SydLabel
                  label='Level'
                  error={hookFormErrorAdapter(form, fieldState)}
                >
                  <Select options={levelOptions} onChange={field.onChange} value={field.value} placeholder='Select:' />
                </SydLabel>
              )}
            />
          </div>
          <br />
          <div className={classes.inputRow}>
            <Controller
              name='name'
              control={form.control}
              rules={{ required: { value: true, message: 'Name is required' } }}
              render={({ field, fieldState }) => (
                <SydLabel
                  label='Name'
                  required
                  error={hookFormErrorAdapter(form, fieldState)}
                >
                  <SydInput {...field} error={fieldState.error} />
                </SydLabel>
              )}
            />
            <Controller
              name='description'
              control={form.control}
              render={({ field, fieldState }) => (
                <SydLabel label='Description'>
                  <SydInput {...field} error={fieldState.error} />
                </SydLabel>
              )}
            />
          </div>
          <br />
          <div className={classes.inputRow}>
            <Controller
              name='code'
              control={form.control}
              rules={{ required: { value: true, message: 'Code is required' } }}
              render={({ field, fieldState }) => (
                <SydLabel
                  label='Code'
                  required
                  error={hookFormErrorAdapter(form, fieldState)}
                >
                  <SydInput {...field} error={fieldState.error} />
                </SydLabel>
              )}
            />
            <Controller
              name='category'
              control={form.control}
              rules={{
                validate: (value) => {
                  if (value === '' && !customCategory) {
                    return 'Category is required'
                  }
                  return true
                }
              }}
              render={({ field, fieldState }) => (
                <SydLabel
                  label='Category'
                  required
                  error={hookFormErrorAdapter(form, fieldState)}
                >
                  <Select
                    options={categoryOptions}
                    onChange={(value) => {
                      field.onChange(value)
                      if (value !== '') {
                        setCustomCategory('')
                      }
                    }}
                    value={field.value}
                    placeholder='Select:'
                    className={classes.selectField}
                  />
                </SydLabel>
              )}
            />
          </div>
          {showCustomInput ? (
            <>
              <div className={classes.inputRow} style={{ marginTop: '1rem' }}>
                <SydInput
                  fullWidth
                  value={customCategory}
                  onChange={(e) => setCustomCategory(e.target.value)}
                  placeholder='Enter custom category'
                  minWidth='0'
                />
              </div>
              {isError ? (
                <div className={classes.inputRow}>
                  <ErrorDisplay error={error} />
                </div>
              ) : null}

              <div className={classes.inputRow} style={{ marginTop: '1rem' }}>
                <Controller
                  name='categoryDescription'
                  control={form.control}
                  rules={{
                    required: {
                      value: showCustomInput,
                      message: 'Category description is required'
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <SydLabel
                      label='Category Description'
                      required={showCustomInput}
                      className={classes.fullRow}
                      error={hookFormErrorAdapter(form, fieldState)}
                    >
                      <SydInput
                        {...field}
                        placeholder='Enter category description'
                      />
                    </SydLabel>
                  )}
                />
              </div>
            </>
          ) : null}

          <div className={classes.buttonRow}>
            <SaveCancelButtons
              containerClassName={classes.saveCancel}
              onSave={form.handleSubmit(onSubmit)}
              onCancel={onClose}
              isSubmitting={isLoading}
            />
          </div>
        </>
      )}
    </SydModal>
  )
}

ThingAddModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func
}

export default ThingAddModal
